import { useState } from 'react';
import { useAuth } from '../lib/auth-context';
import { supabase } from '../lib/supabase';
import { deleteAccount } from '../lib/api';

export function SettingsPage() {
  const { user, session, signOut } = useAuth();
  const [displayName, setDisplayName] = useState(user?.user_metadata?.display_name ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);

    const { error: updateError } = await supabase.auth.updateUser({
      data: { display_name: displayName },
    });

    if (updateError) {
      setError(updateError.message);
    } else {
      setSaved(true);
    }
    setSaving(false);
  };

  const handleDeleteAccount = async () => {
    if (!session || deleteConfirm !== 'DELETE') return;
    setDeleting(true);
    setError(null);

    const res = await deleteAccount(session.access_token);
    if (!res.ok) {
      const body = (await res.json()) as { error: string };
      setError(body.error);
      setDeleting(false);
      return;
    }

    await signOut();
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">Settings</h2>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">{error}</div>}
      {saved && <div className="mb-4 p-3 bg-green-50 text-green-700 rounded text-sm">Profile updated.</div>}

      {/* Profile settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h3 className="font-semibold mb-4">Profile</h3>
        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Email</label>
            <p className="mt-1 text-gray-900">{user?.email}</p>
          </div>
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="mt-1 w-full max-w-md px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 bg-sentinel-700 text-white rounded-md hover:bg-sentinel-900 disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </form>
      </div>

      {/* Danger zone */}
      <div className="bg-white rounded-lg shadow p-6 border border-red-200">
        <h3 className="font-semibold text-red-700 mb-4">Danger Zone</h3>
        <p className="text-sm text-gray-600 mb-4">
          Deleting your account will soft-delete your tenant record and revoke all OAuth tokens.
          This action cannot be undone from the dashboard.
        </p>
        <div className="flex gap-4 items-end">
          <div>
            <label htmlFor="deleteConfirm" className="block text-sm font-medium text-gray-700">
              Type DELETE to confirm
            </label>
            <input
              id="deleteConfirm"
              type="text"
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              className="mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
          </div>
          <button
            onClick={handleDeleteAccount}
            disabled={deleting || deleteConfirm !== 'DELETE'}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 text-sm"
          >
            {deleting ? 'Deleting...' : 'Delete Account'}
          </button>
        </div>
      </div>
    </div>
  );
}
